import React, { useEffect, useState } from 'react'

import getMakes, { GetMakesOptions, Make } from 'api/vehicles/getMakes'
import { FormikDropdown } from 'components/FormikFields'
import { FieldKit } from 'containers/FormBuilder'
import { FormikValues } from 'formik'
import AssetSubtype from 'state/enums/AssetSubtype'
import AssetType from 'state/enums/AssetType'

interface BaseMakeDropdownProps {
    assetSubType?: AssetSubtype
    assetType?: AssetType
    dataSource?: string[]
    disable?: boolean
    fieldNamePrefix: string
    formik: FieldKit<FormikValues>
    label?: string
    placeholder?: string
    prefilled?: boolean
    primaryVehicleId?: number
    searchable?: boolean
    searchPlaceholder?: string
    captive?: string
}

const defaultProps = {
    dataSource: null,
    assetSubType: null,
    disable: false,
    primaryVehicleId: null,
    assetType: '',
    boatType: '',
    searchable: false,
    searchPlaceholder: 'Search...',
    captive: undefined,
}

const getNestedValue = (obj: FormikValues, path: string) => {
    return path.split('.').reduce((acc, part) => {
        const arrayMatch = /^([^[]+)\[(\d+)\]$/.exec(part)

        if (arrayMatch) {
            const [, arrayName, index] = arrayMatch

            return acc?.[arrayName]?.[parseInt(index, 10)]
        }

        return acc?.[part]
    }, obj)
}

const getAssetSpecificParams = (
    assetType: AssetType,
    formik: FieldKit<FormikValues>,
    assetSubType?: AssetSubtype,
    primaryVehicleId?: number,
) => {
    if (assetType === AssetType.MARINE) {
        const { marine } = formik.values

        if (assetSubType === AssetSubtype.MARINE_HULL) {
            return { boatType: marine.hull.boatType }
        }

        if (assetSubType === AssetSubtype.MARINE_ENGINE) {
            return {}
        }

        throw new Error('Invalid asset subtype')
    }

    return {
        primaryVehicleId,
    }
}

const getDropdownLabel = (
    assetType?: AssetType,
    assetSubType?: AssetSubtype,
) => {
    if (assetType === AssetType.MARINE) {
        if (assetSubType === AssetSubtype.MARINE_HULL) {
            return 'Hull Make'
        }

        if (assetSubType === AssetSubtype.MARINE_ENGINE) {
            return 'Motor Make'
        }
    }

    return 'Make'
}

export default function BaseMakeDropdown({
    formik,
    dataSource,
    assetType,
    assetSubType,
    disable,
    primaryVehicleId,
    fieldNamePrefix,
    label,
    placeholder,
    prefilled,
    searchable,
    searchPlaceholder,
    captive,
}: Readonly<BaseMakeDropdownProps>) {
    const [response, updateResponse] = useState([] as Make[])
    const values = getNestedValue(formik.values, fieldNamePrefix)
    const { condition, year, make } = values
    const fieldName = `${fieldNamePrefix}.make`

    const extraParams = getAssetSpecificParams(
        assetType as AssetType,
        formik,
        assetSubType as AssetSubtype,
        primaryVehicleId,
    )

    useEffect(() => {
        const abortController = new AbortController()

        if (condition && year) {
            const getMakesOptions = {
                condition,
                year,
                dataSource,
                assetType,
                assetSubType,
                disable,
                captive,
                ...extraParams,
            } as GetMakesOptions

            getMakes(getMakesOptions, abortController)
                .then((makes: Make[]) => {
                    updateResponse(makes)
                    if (
                        makes.filter((e) => `${e.name}` === make).length === 0
                    ) {
                        formik.setFieldValue(fieldName, '')
                    }
                })
                .catch(() => {})
        }

        return () => abortController.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition, year])

    if (!year || year === '0') {
        return (
            <FormikDropdown
                name={fieldName}
                label={label ?? 'Make'}
                placeholder={placeholder ?? 'Select One'}
                disabled
                prefilled={prefilled}
                searchable={searchable}
                searchPlaceholder={searchPlaceholder}
            >
                {[{ name: '', value: '' }]}
            </FormikDropdown>
        )
    }

    return (
        <FormikDropdown
            name={fieldName}
            label={label ?? getDropdownLabel(assetType, assetSubType)}
            placeholder={placeholder ?? 'Select One'}
            disabled={response.length === 0}
            error={make === null || make === ''}
            prefilled={prefilled}
            searchable={searchable}
            searchPlaceholder={searchPlaceholder}
        >
            {response.map((child: Make) => ({
                name: child.name,
                value: child.name,
            }))}
        </FormikDropdown>
    )
}

BaseMakeDropdown.defaultProps = defaultProps
